import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ENVIRONMENT_LABEL } from "@/components/app-registration-badges";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type SignInConsentItem, useMarkSignInApproved } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

/** `MarkSignInConsentRequest.note` bounds, mirrored so the form fails before the round trip. */
const MIN_NOTE_LENGTH = 3;
const MAX_NOTE_LENGTH = 500;

interface MarkApprovedDialogProps {
  item: SignInConsentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MarkApprovedDialog({ item, open, onOpenChange }: MarkApprovedDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {item && open && (
          // Remount per registration so a note never carries into another one.
          <MarkApprovedForm key={item.request_id} item={item} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The by-hand fallback for a consent Scaffold UI could not confirm itself —
 * normally it does, from the grant, within a few minutes — most often because
 * it may not read grants.
 *
 * Says plainly that it changes the record and nothing in Entra — marking a
 * registration nobody consented to does not make sign-in work, it only hides
 * the reason it fails.
 */
function MarkApprovedForm({ item, onClose }: { item: SignInConsentItem; onClose: () => void }) {
  const mark = useMarkSignInApproved();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = mark.isPending;
  const trimmed = note.trim();
  const canSubmit = trimmed.length >= MIN_NOTE_LENGTH && !pending;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setError(null);
    try {
      await mark.mutateAsync({
        requestId: item.request_id,
        projectId: item.project_id,
        note: trimmed,
      });
      logger.debug("[it-approvals] marked approved by hand:", item.request_id);
      toast.success(`${item.display_name} is recorded as approved by IT.`);
      onClose();
    } catch (err) {
      logger.error("[it-approvals] mark approved failed:", err);
      setError(readApiErrorMessage(err, "Couldn't record the approval. Please try again."));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Mark as approved by IT</DialogTitle>
        <DialogDescription>
          <strong>{item.display_name}</strong> for {item.project_name} (
          {ENVIRONMENT_LABEL[item.environment]}).
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
            <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              This only changes Scaffold UI&apos;s record, not Entra
            </p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
              Scaffold UI normally confirms consent by itself within a few minutes. Use this only
              when IT has granted tenant-wide consent and it still isn&apos;t confirmed &mdash; for
              example because Scaffold UI isn&apos;t allowed to read consent. If nobody has actually
              consented, sign-in still fails after this. Your name and the note are recorded in the
              activity log.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mark-approved-note">How do you know it was approved? (required)</Label>
            <Textarea
              id="mark-approved-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Granted by Jane Doe in the Entra portal, ticket INC-12345."
              rows={3}
              maxLength={MAX_NOTE_LENGTH}
              disabled={pending}
            />
            <p className="text-muted-foreground text-xs">At least {MIN_NOTE_LENGTH} characters.</p>
          </div>

          {error && (
            <p className="text-destructive flex items-start gap-2 text-sm">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {pending ? "Recording…" : "Mark as approved"}
          </Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}
